import { Box, TextField } from "@mui/material";
import type { DetailedSolutionApiResponse } from "../../../types";
import { LinkToStripe } from "../types";
import ReadOnlyCarbonOffsetting from "../../readOnlyComponents/ReadOnlyCarbonOffsetting";
import { ReadOnlyCoordinates } from "../../readOnlyComponents/ReadOnlyCoordinates";
import { ReadOnlyCountries } from "../../readOnlyComponents/ReadOnlyCountry";
import { ReadOnlyDates } from "../../readOnlyComponents/ReadOnlyDates";
import ReadOnlyBenefitsContainer from "../../readOnlyComponents/ReadOnlyBenefitsContainer";
import ReadOnlyCommercialInfoContainer from "../../readOnlyComponents/ReadOnlyCommercialInfoContainer";
import ReadOnlyInternalDetailsContainer from "../../readOnlyComponents/ReadOnlyInternalDetailsContainer";
import ReadOnlyInterventionsContainer from "../../readOnlyComponents/ReadOnlyInterventionsContainer";
import ReadOnlyOutcomesContainer from "../../readOnlyComponents/ReadOnlyOutcomesContainer";
import ReadOnlyNarrativeContainer from "../../readOnlyComponents/ReadOnlyNarrativeContainer";
import ReadOnlyProgrammeInfoContainer from "../../readOnlyComponents/ReadOnlyProgrammeInfoContainer";
import ReadOnlySatelliteImageryContainer from "../../readOnlyComponents/ReadOnlySatelliteImageryContainer";
import ReadOnlyScoresContainer from "../../readOnlyComponents/ReadOnlyScoresContainer";
import ReadOnlyIntegrityScoresContainer from "../../readOnlyComponents/ReadOnlyIntegrityScoresContainer";
import { ReadOnlyReadyForSaleRadio } from "../../readOnlyComponents/ReadOnlyReadyForSaleRadio";
import { ReadOnlySDG } from "../../readOnlyComponents/ReadOnlySDG";
import { emptyOutcomes } from "../../../formatting/defaults";
import {
  getFormattedDevelopmentStage,
  getReadOnlyInterventions,
  getReadOnlyProgrammeInfo,
  getReadOnlyInternalDetails,
} from "../../../formatting/domain-value-manipulation";
import { ReadOnlyAssessmentReportContainer } from "../../readOnlyComponents/ReadOnlyAssessmentReportContainer";
import ReadOnlyAssessmentUniqueBenefitsContainer from "../../readOnlyComponents/ReadOnlyAssessmentUniqueBenefitsContainer";

function AllFieldsTab({
  details,
  linkToStripe,
}: {
  details: DetailedSolutionApiResponse;
  linkToStripe: LinkToStripe;
}) {
  const {
    id,
    vbcWebsiteDisplayName,
    developer,
    shortDescription,
    assessmentMethodology,
    interventionType,
    country,
    isReadyForSale,
    notReadyForSaleReason,
    isUsedForCarbonOffsetting,
    SDG,
    developmentStage,
    date,
    coordinates,
    interventions,
    outcomes,
    programmeInfo,
    internalUseOnly,
    pillars,
    commercialInformation,
    satelliteImagery,
    locationPinMapLink,
    narrative,
    assessmentReport,
  } = details;

  const formattedShortDescription = shortDescription || "-";
  const formattedDevelopmentStage =
    getFormattedDevelopmentStage(developmentStage);
  const readOnlyInterventions = getReadOnlyInterventions(interventions);
  const readOnlyProgrammeInfo = getReadOnlyProgrammeInfo(programmeInfo);
  const readOnlyInternalDetails = getReadOnlyInternalDetails(internalUseOnly);

  return (
    <Box sx={{ paddingBottom: 4 }}>
      <Box sx={{ paddingTop: 1, paddingRight: 2 }}>
        <ReadOnlyCountries code={country} />
      </Box>
      <Box sx={{ display: "flex", direction: "row", paddingBottom: 2 }}>
        <Box sx={{ paddingRight: 2 }}>
          <ReadOnlyReadyForSaleRadio
            isReady={isReadyForSale}
            reason={notReadyForSaleReason}
          />
        </Box>
        <Box sx={{ paddingRight: 2 }}>
          <ReadOnlyCarbonOffsetting
            usedToOffset={Boolean(isUsedForCarbonOffsetting)}
          />
        </Box>
      </Box>
      <Box sx={{ paddingBottom: 1 }} maxWidth="fit-content">
        <ReadOnlySDG sdg={SDG} />
      </Box>
      <TextField
        value={id}
        variant="standard"
        id="solution-id"
        label="ID"
        size="small"
        fullWidth
        sx={{
          marginBottom: 2,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />
      <TextField
        value={vbcWebsiteDisplayName || "-"}
        variant="standard"
        id="solution-vbc-website-display-name"
        label="VBC Website Display Name"
        size="small"
        fullWidth
        sx={{
          marginBottom: 2,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />
      <TextField
        value={formattedShortDescription}
        variant="standard"
        multiline
        rows={formattedShortDescription === "-" ? 1 : 4}
        id="solution-short-description"
        label="Short Description"
        size="small"
        sx={{
          marginBottom: 2,
          minWidth: "100%",
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />
      <TextField
        value={developer || "-"}
        variant="standard"
        id="solution-developer"
        label="Developer"
        size="small"
        fullWidth
        sx={{
          marginBottom: 2,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />
      <TextField
        value={formattedDevelopmentStage}
        variant="standard"
        id="solution-development-stage"
        label="Development Stage"
        size="small"
        fullWidth
        sx={{
          marginBottom: 2,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />

      <TextField
        value={assessmentMethodology || "-"}
        variant="standard"
        id="solution-assessment-methodology"
        label="Assessment Methodology"
        size="small"
        fullWidth
        sx={{
          marginBottom: 2,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />
      <TextField
        value={interventionType || "-"}
        variant="standard"
        id="solution-intervention-type"
        label="Intervention Type"
        size="small"
        fullWidth
        sx={{
          marginBottom: 2,
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
          },
        }}
      />
      <Box sx={{ paddingBottom: 2 }}>
        <ReadOnlyDates
          start={date?.start as unknown as number}
          end={date?.end as unknown as number}
        />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyCoordinates
          latitude={coordinates?.latitude as unknown as string}
          longitude={coordinates?.longitude as unknown as string}
        />
      </Box>

      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyInterventionsContainer interventions={readOnlyInterventions} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyBenefitsContainer pillars={pillars} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyScoresContainer pillars={pillars} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyIntegrityScoresContainer pillars={pillars} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyOutcomesContainer outcomes={outcomes ?? emptyOutcomes} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyProgrammeInfoContainer programmeInfo={readOnlyProgrammeInfo} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyCommercialInfoContainer
          commercialInformation={commercialInformation}
        />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlySatelliteImageryContainer
          satelliteImagery={satelliteImagery}
          locationPinMapLink={locationPinMapLink}
        />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyNarrativeContainer narrative={narrative} />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyAssessmentReportContainer
          assessmentReport={assessmentReport}
        />
      </Box>
      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyAssessmentUniqueBenefitsContainer
          benefits={assessmentReport?.benefits}
        />
      </Box>

      <Box sx={{ paddingBottom: 4 }}>
        <ReadOnlyInternalDetailsContainer
          internalDetails={readOnlyInternalDetails}
          linkToStripe={linkToStripe}
        />
      </Box>
    </Box>
  );
}

export default AllFieldsTab;
